/**
 * Furniture catalog loader — fetches the list of SVG filenames from the public
 * JSON file and groups them by room category (matching the reference grouping).
 */

const FURNITURE_LIST = [
	'Bed2.svg',
	'airflow.svg',
	'bathtub 2.svg',
	'bathtub.svg',
	'bed3.svg',
	'cabinet 2.svg',
	'cabinet 3.svg',
	'cabinet.svg',
	'chair1.svg',
	'chair2.svg',
	'chair4.svg',
	'chair5.svg',
	'chair6.svg',
	'chair7.svg',
	'char3.svg',
	'coffee table.svg',
	'coffeeTable.svg',
	'corner couch.svg',
	'corner desk.svg',
	'cornerDesk.svg',
	'couch.svg',
	'couch1.svg',
	'couch3.svg',
	'couch5.svg',
	'desk.svg',
	'diningTable1.svg',
	'diningTable2.svg',
	'diningTable3.svg',
	'diningTableWithChairs.svg',
	'doubleBed.svg',
	'doubleSink.svg',
	'jakuzi.svg',
	'kitchen cabinet 2.svg',
	'kitchen cabinet.svg',
	'lampCabinet.svg',
	'longCabinet.svg',
	'officeChair.svg',
	'officeChair2.svg',
	'plant1.svg',
	'plant2.svg',
	'roundTable.svg',
	'sink 2.svg',
	'sink.svg',
	'sink1.svg',
	'sink2.svg',
	'smallCabinet.svg',
	'smallCornerCouch.svg',
	'smallCouch.svg',
	'smallTable.svg',
	'sofa1.svg',
	'sofa2.svg',
	'sofa4.svg',
	'sofa5.svg',
	'stove.svg',
	'stove1.svg',
	'table.svg',
	'tableDining.svg',
	'toilet 2.svg',
	'toilet 3.svg',
	'toilet.svg',
	'toilet1.svg',
	'tv.svg',
	'tvCabinet 2.svg',
	'tvCabinet.svg',
];

export interface FurnitureCatalogItem {
	/** SVG filename, e.g. "chair1.svg". Path is /images/furnitures/{src}. */
	src: string;
	/** Human-readable display name derived from the filename. */
	name: string;
}

export interface FurnitureGroup {
	group: string;
	items: FurnitureCatalogItem[];
}

const GROUP_ORDER: string[] = [
	'Beds',
	'Chairs',
	'Sofas & couches',
	'Tables',
	'Desks',
	'Cabinets & storage',
	'Kitchen',
	'Bathroom',
	'TV & entertainment',
	'Plants & decor',
	'Other',
];

function getGroup(filename: string): string {
	const n = filename.replace(/\.(svg|png|jpg)$/i, '').toLowerCase();
	if (/^bed|doublebed/.test(n)) return 'Beds';
	if (/chair|char\d/.test(n)) return 'Chairs';
	if (/sofa|couch|corner.*couch|small.*couch/.test(n)) return 'Sofas & couches';
	if (/table|coffee|dining|roundtable|smalltable/.test(n)) return 'Tables';
	if (/desk|cornerdesk/.test(n)) return 'Desks';
	if (/cabinet|lampcabinet|longcabinet|smallcabinet|tvcabinet|kitchen cabinet/.test(n))
		return 'Cabinets & storage';
	if (/stove|airflow/.test(n)) return 'Kitchen';
	if (/bathtub|toilet|sink|doublesink|jakuzi/.test(n)) return 'Bathroom';
	if (n === 'tv') return 'TV & entertainment';
	if (/plant/.test(n)) return 'Plants & decor';
	return 'Other';
}

/** Derives a display name from the SVG filename. */
function toDisplayName(filename: string): string {
	return filename
		.replace(/\.(svg|png|jpg)$/i, '')
		.replace(/[-_]/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

let cached: FurnitureGroup[] | null = null;

export async function loadFurnitureCatalog(): Promise<FurnitureGroup[]> {
	if (cached) return cached;

	try {
		cached = buildGroups(FURNITURE_LIST);
		return cached;
	} catch (err) {
		console.error('[furniture-catalog] Failed to load catalog:', err);
		return [];
	}
}

function buildGroups(files: string[]): FurnitureGroup[] {
	const byGroup = new Map<string, FurnitureCatalogItem[]>();

	for (const file of files) {
		const group = getGroup(file);
		if (!byGroup.has(group)) byGroup.set(group, []);
		byGroup.get(group)!.push({ src: file, name: toDisplayName(file) });
	}

	// Sort items within each group alphabetically
	for (const items of byGroup.values()) {
		items.sort((a, b) => a.name.localeCompare(b.name));
	}

	return GROUP_ORDER.filter((g) => byGroup.has(g)).map((group) => ({
		group,
		items: byGroup.get(group)!,
	}));
}
